import React, { useEffect, useState } from "react";
import AppLayout from "@/components/layout/AppLayout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileText, FileSpreadsheet } from "lucide-react";
import { api, API } from "@/lib/api";

const DEFS = [
  { title: "Daily Production Report", desc: "Per-day shift-wise production with feedstock, AFR, NCV, moisture and totals." },
  { title: "Weekly Production Summary", desc: "Last 7 days aggregated production and energy consumption." },
  { title: "Monthly MIS Report", desc: "Full-month KPIs, breakdowns, maintenance compliance and inventory value." },
  { title: "Custom Date Range", desc: "Pick any start and end date to generate the production report." },
];

export default function Reports() {
  const [projects, setProjects] = useState([]);
  const [pid, setPid] = useState("all");
  const today = new Date();
  const fmt = (d) => d.toISOString().slice(0, 10);
  const [start, setStart] = useState(fmt(new Date(today.getTime() - 7 * 86400000)));
  const [end, setEnd] = useState(fmt(today));

  useEffect(() => { api.get("/projects").then((r) => setProjects(r.data)); }, []);

  const download = (kind) => {
    const t = localStorage.getItem("neipl_token");
    const qs = new URLSearchParams();
    if (pid !== "all") qs.set("project_id", pid);
    if (start) qs.set("start", start);
    if (end) qs.set("end", end);
    const url = `${API}/reports/production/${kind}?${qs.toString()}`;
    fetch(url, { headers: { Authorization: `Bearer ${t}` } })
      .then((r) => r.blob())
      .then((b) => {
        const u = URL.createObjectURL(b);
        const a = document.createElement("a");
        a.href = u; a.download = `production_report.${kind === "excel" ? "xlsx" : "pdf"}`;
        a.click();
      });
  };

  const preset = (days) => {
    setEnd(fmt(today));
    setStart(fmt(new Date(today.getTime() - days * 86400000)));
  };

  return (
    <AppLayout title="MIS & Reports" subtitle="Generate professional PDF and Excel reports">
      <Card className="p-5 mb-6">
        <div className="label-cap text-slate-500 mb-3">Report parameters</div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <Label className="label-cap">Project</Label>
            <Select value={pid} onValueChange={setPid}>
              <SelectTrigger data-testid="report-project-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All projects</SelectItem>
                {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label className="label-cap">Start Date</Label><Input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="report-start-input" /></div>
          <div><Label className="label-cap">End Date</Label><Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="report-end-input" /></div>
          <div className="flex items-end gap-2">
            <Button variant="outline" size="sm" onClick={() => preset(1)} data-testid="report-preset-daily">Daily</Button>
            <Button variant="outline" size="sm" onClick={() => preset(7)} data-testid="report-preset-weekly">Weekly</Button>
            <Button variant="outline" size="sm" onClick={() => preset(30)} data-testid="report-preset-monthly">Monthly</Button>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {DEFS.map((d) => (
          <Card key={d.title} className="p-5 hover:shadow-md transition" data-testid={`report-card-${d.title.toLowerCase().replace(/[^a-z]/g, "-")}`}>
            <div className="label-cap text-slate-500">Report</div>
            <h3 className="text-lg font-semibold mt-0.5" style={{ fontFamily: "Chivo" }}>{d.title}</h3>
            <p className="text-sm text-slate-600 mt-2 mb-4">{d.desc}</p>
            <div className="flex gap-2">
              <Button onClick={() => download("pdf")} className="bg-sky-900 hover:bg-sky-950" data-testid={`report-pdf-${d.title.toLowerCase().replace(/[^a-z]/g, "-")}`}>
                <FileText className="h-3.5 w-3.5 mr-1" /> Download PDF
              </Button>
              <Button onClick={() => download("excel")} variant="outline" data-testid={`report-excel-${d.title.toLowerCase().replace(/[^a-z]/g, "-")}`}>
                <FileSpreadsheet className="h-3.5 w-3.5 mr-1" /> Download Excel
              </Button>
            </div>
          </Card>
        ))}
      </div>
    </AppLayout>
  );
}
